import { useEffect } from 'react'
import type { RefObject } from 'react'
import { motion } from '@/utils/motion'
import { useIsPhone } from '@/hooks/useIsPhone'

/** Breathing room left between the trigger and the top edge of the sheet. */
const GAP = 12

/**
 * Keeps the control that opened a bottom sheet visible above it.
 *
 * On a phone the sheet covers the lower part of the screen, so tapping a word or a
 * button near the bottom hides the very thing you just touched. This pads the page by
 * the sheet's height — giving the document somewhere to scroll to — and then slides the
 * trigger up clear of the sheet, so the learner keeps their place instead of hunting
 * for it after closing.
 *
 * No-op above phone width, where the popover is anchored to its trigger anyway.
 */
export function useSheetAnchor(
  open: boolean,
  anchor: RefObject<HTMLElement | null> | DOMRect | null,
  sheetRef: RefObject<HTMLElement | null>,
): void {
  const isPhone = useIsPhone()

  useEffect(() => {
    if (!open || !isPhone) return

    const sheet = sheetRef.current
    if (!sheet) return

    const sheetHeight = sheet.getBoundingClientRect().height
    if (sheetHeight <= 0) return

    const previousPadding = document.body.style.paddingBottom
    document.body.style.paddingBottom = `${Math.round(sheetHeight)}px`

    const rect = resolve(anchor)
    if (rect) {
      const anchorBottom = rect.bottom
      const clearOf = window.innerHeight - sheetHeight - GAP
      const overlap = anchorBottom - clearOf
      if (overlap > 0) {
        window.scrollBy({ top: overlap, behavior: motion.enabled() ? 'smooth' : 'auto' })
      }
    }

    return () => { document.body.style.paddingBottom = previousPadding }
  }, [open, isPhone, anchor, sheetRef])
}

/** Word popovers hand over the tapped word's rect; everything else holds a ref. */
function resolve(anchor: RefObject<HTMLElement | null> | DOMRect | null): DOMRect | null {
  if (!anchor) return null
  if ('current' in anchor) return anchor.current?.getBoundingClientRect() ?? null
  return anchor
}
